import { Injectable } from "@angular/core";
import { ConfigServiceValue } from "@fabric-types/svc-config";
import { Jurisdiction, ObgConfigFeAdaptiveClientCore } from "@obg/core.config";
import { ConfigService, DocumentRef, FeatureFlagService, fromMinutesToMilliseconds } from "@obg/ngx.global";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { LoginDisallowedFeatureFlag, LOGIN_DISALLOWED_POKER_KEY } from "./session.const";
import { CustomerCountryRedirectionConfig } from "./session.model";

const CURRENT_SESSION_ENDPOINT = "v1/current-session";

@Injectable({
	providedIn: "root",
})
export class SessionConfigService {
	constructor(
		private configService: ConfigService<ObgConfigFeAdaptiveClientCore>,
		private featureFlagService: FeatureFlagService,
		private documentRef: DocumentRef
	) {}

	getLoginEndpoint(): string {
		return this.configService.get((x) => x.core.auth.endpoints!.login, "v1/sessions/");
	}

	getLogoutEndpoint(): string {
		return this.configService.get((x) => x.core.auth.endpoints!.logout, `${CURRENT_SESSION_ENDPOINT}`);
	}

	getSessionEndpoint(): string {
		return this.configService.get((x) => x.core.auth.endpoints!.session, `${CURRENT_SESSION_ENDPOINT}`);
	}

	getKeepAliveEndpoint(): string {
		return this.configService.get((x) => x.core.auth.endpoints!.keepAlive, `${CURRENT_SESSION_ENDPOINT}`);
	}

	getKeepAliveInterval(): number {
		return this.configService.get((x) => x.core.auth.keepAliveInterval, fromMinutesToMilliseconds(5));
	}

	isKeepAliveEnabled(): boolean {
		return this.configService.get((x) => x.core.auth.keepAliveEnabled, true);
	}

	getSingleSignOnSessionTokenEndpoint(): string {
		return this.configService.get(
			(x) => x.core.auth.endpoints!.singleSignOnSessionToken,
			"v1/single-sign-on-sessions/sessiontoken"
		);
	}

	getNextTimeLoginEndpoint(): string {
		return this.configService.get(
			(x) => x.core.auth.endpoints!.nextTimeLogin,
			"v2/currentcustomer/next-time-login/"
		);
	}

	/**
	 * This config is under jurisdiction
	 */
	getNextTimeLoginEnabled(): boolean {
		return this.configService.get((x) => x.core.auth.nextTimeLoginEnabled, false);
	}

	isBrandRedirectionEnabled(): boolean {
		return this.configService.get((x) => x.core.auth?.isBrandRedirectionEnabled, false);
	}

	getRedirectTemplate(): Partial<Record<Jurisdiction, string>> {
		return this.configService.get((x) => x.core.auth?.redirectionTemplates, {});
	}

	getDefaultRedirectionTemplate(): string {
		return this.configService.get((x) => x.core.auth?.defaultRedirectionTemplate, "https://www.{{domain}}");
	}

	getRedirectionDomainByJurisdiction(): Partial<Record<Jurisdiction, string>> {
		return this.configService.get((x) => x.core.auth?.redirectionDomainByJurisdiction, {});
	}

	getRedirectionDomainByCustomerCountry(): CustomerCountryRedirectionConfig[] {
		return this.configService.get((x) => x.core.auth?.redirectionDomainByCustomerCountry, []);
	}

	getRedirectionDisabledCustomerCountries(): string[] {
		return this.configService.get((x) => x.core.auth?.redirectionDisabledCustomerCountries, []);
	}

	getLoginDisallowedCountries(): string[] {
		return this.configService.get((x) => {
			return x.core.auth?.loginDisallowedCountries;
		}, []);
	}

	isLoginDisallowedForCountries(): boolean {
		return this.getLoginDisallowedCountries().length > 0;
	}

	isPokerDisallowedLoginEnabled(): boolean {
		const featureFlag = this.featureFlagService.get<LoginDisallowedFeatureFlag | undefined>(
			LOGIN_DISALLOWED_POKER_KEY
		);
		return featureFlag?.enabled ?? false;
	}

	getLoginAllowedCountries(): string[] {
		return this.configService.get((x) => {
			return x.core.auth?.loginAllowedCountries;
		}, []);
	}

	getPokerLoginBlockedCountries(): string[] {
		return this.configService.get((x) => {
			return x.core.auth?.pokerloginBlockedCountries;
		}, []);
	}

	isLoginAllowedForCountries(): boolean {
		return this.getLoginAllowedCountries().length > 0;
	}

	isCountryBlockingEnabled(): boolean {
		return this.isLoginAllowedForCountries() || this.isLoginDisallowedForCountries();
	}

	isAuthTransactionCallNeeded(): boolean {
		return this.configService.get((x) => x.core.auth?.isAuthTransactionCallNeeded, false);
	}

	isEnabledAwsThreatDetection$(): Observable<boolean | undefined> {
		return new Observable(this.documentRef.obgEventBus.retrieve(ConfigServiceValue.Config)).pipe(
			map((config) => config["svc-platform"]?.awsThreatDetection?.isEnabled)
		);
	}

	isGeofencingEnabled(): boolean {
		return this.configService.get((x) => x.core.auth?.isGeofencingEnabled, false);
	}
}
